// BlogPostPage.js - Individual blog post page for SEO and direct linking
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URL } from '../api';
import './BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog-posts/${slug}/`);
        if (!response.ok) {
          throw new Error('Blog post not found');
        }
        const data = await response.json();
        setPost(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Blog post not found');
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Update document title and meta tags for SEO
  useEffect(() => {
    if (post) {
      // Update page title
      document.title = `${post.title} | Christensen Kirtis Publishing`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        const description = post.excerpt || post.content?.replace(/<[^>]*>/g, '').substring(0, 160);
        metaDescription.setAttribute('content', description);
      }
      
      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      
      if (ogTitle) ogTitle.setAttribute('content', post.title);
      if (ogDescription) ogDescription.setAttribute('content', post.excerpt || '');
      if (ogUrl) ogUrl.setAttribute('content', `https://publishing.christensenkirtis.com/blog/${post.slug}`);
      if (post.featured_image_url && ogImage) ogImage.setAttribute('content', post.featured_image_url);
      if (twitterTitle) twitterTitle.setAttribute('content', post.title);
      if (twitterDescription) twitterDescription.setAttribute('content', post.excerpt || '');
      if (post.featured_image_url && twitterImage) twitterImage.setAttribute('content', post.featured_image_url);
    }
    
    return () => {
      document.title = 'Christensen Kirtis Publishing';
    };
  }, [post]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-page">
        <div className="error">
          <h2>Blog Post Not Found</h2>
          <p>Sorry, the blog post you're looking for doesn't exist.</p>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="blog-post-page">
      <article className="blog-post-content">
        <Link to="/" className="back-link">← Back to Home</Link>
        
        {post.featured_image_url && (
          <div className="featured-image">
            <img src={post.featured_image_url} alt={post.featured_image_alt || post.title} />
          </div>
        )}
        
        <header className="post-header">
          <h1>{post.title}</h1>
          <div className="post-meta">
            {post.author_name && <span className="author">By {post.author_name}</span>}
            {post.published_at && <span className="date">{formatDate(post.published_at)}</span>}
            {post.category && <span className="category">{post.category}</span>}
          </div>
        </header>
        
        <div 
          className="post-body"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        
        {post.tags && post.tags.length > 0 && (
          <div className="post-tags">
            {post.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        )}
        
        {post.related_product && (
          <div className="related-product">
            <h3>Related Product</h3>
            <Link to={`/products/${post.related_product.id}`}>
              {post.related_product.title}
            </Link>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;
